"use client";
import { useEffect, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import logo from "../../../public/loghi/logo.png";
import { NewsApiResponse, NewsItem, mapNews } from "@/types/news";

type NewsApiWithCategory = NewsApiResponse & { category?: string };

type NewsCardItem = NewsItem & {
  category: string;
  date: string;
};

function NewsCard({
  item,
  delay = 0,
  onClick,
}: {
  item: NewsCardItem;
  delay?: number;
  onClick: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
    >
      <Card
        onClick={onClick}
        role="button"
        tabIndex={0}
        aria-label={`Apri news: ${item.title}`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        className="group cursor-pointer transition bg-(--background) rounded-2xl p-6 flex flex-col justify-between items-start w-[90vw] max-w-[500px] mx-auto border border-(--card-foreground) hover:shadow-md hover:-translate-y-0.5"
      >
        <CardContent className="p-0 w-full">
          <div className="uppercase text-sm font-semibold text-(--card-foreground) mb-2">
            {item.category}
            <span className="text-gray-400 font-normal ml-2">• {item.date}</span>
          </div>
          
          <h3 className="text-lg font-bold text-(--foreground) group-hover:underline mb-2">
            {item.title}
          </h3>

          {item.description && (
            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
              {item.description}
            </p>
          )}
        </CardContent>

        <ArrowUpRight
          className="mt-3 text-(--card-foreground) opacity-0 group-hover:opacity-100 transition duration-200"
          size={20}
        />
      </Card>
    </motion.div>
  );
}

export default function HomeNewsSection() {
  const [news, setNews] = useState<NewsCardItem[]>([]);
  const [ready, setReady] = useState(false);
  const router = useRouter();

  useEffect(() => {
    let alive = true;
    const minDelay = new Promise((r) => setTimeout(r, 700));

    const fetchNews = fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/news`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    Promise.all([minDelay, fetchNews])
      .then(async ([_, res]) => {
        if (!res.ok) throw new Error("Errore nel caricamento delle news");
        const raw = (await res.json()) as NewsApiWithCategory[];
        if (!alive) return;

        const combined = raw.map((api) => ({ api, ui: mapNews(api) }));

        const mapped: NewsCardItem[] = combined
          .sort(
            (a, b) =>
              new Date(b.ui.createdAt ?? 0).getTime() -
              new Date(a.ui.createdAt ?? 0).getTime()
          )
          .slice(0, 2)
          .map(({ api, ui }) => ({
            ...ui,
            category: api.category ?? "News",
            date: ui.createdAt
              ? new Date(ui.createdAt).toLocaleDateString("it-IT")
              : "Data sconosciuta",
          }));

        setNews(mapped);
      })
      .catch(() => {
        if (alive) setNews([]);
      })
      .finally(() => {
        if (alive) setReady(true);
      });

    return () => {
      alive = false;
    };
  }, []);

  return (
    <section className="max-w-[1100px] py-24 px-8 pt-0 md:px-12 flex flex-col lg:flex-row items-stretch gap-10 mx-auto">
      {/* Colonna sinistra */}
      <div className="p-1 flex-1 flex flex-col items-left text-left sm:text-center sm:items-center lg:items-start lg:text-left gap-2">
        <h2 className="text-4xl sm:text-5xl font-extrabold">
          <span className="text-(--foreground)">Le Ultime </span>
          <span className="text-(--card-foreground)">Notizie</span>
        </h2>

        <div className="flex-1 flex flex-col justify-between h-full sm:items-center gap-2 lg:items-start">
          <p className="text-(--foreground) max-w-[400px]">
            Resta aggiornato sulle novità della scuola, eventi e comunicazioni ufficiali.
          </p>

          <Image
            src={logo}
            alt="Logo Santa Marta"
            width={200}
            height={200}
            className="hidden sm:block shrink-0 lg:py-4"
            loading="lazy"
          />
        </div>

        <Button
          className="cursor-pointer bg-(--btn-bg) text-(--btn-foreground) font-semibold text-base sm:text-lg md:text-xl px-6 py-4 sm:px-8 sm:py-4 shadow-md transition-all duration-300 hover:bg-(--btn-hover) hover:shadow-lg"
          onClick={() => router.push("/news")}
        >
          Scopri tutte le news!
        </Button>
      </div>

      {/* Colonna destra */}
      <div className="relative flex-1 w-full">
        <div className="flex flex-col justify-between h-full items-center lg:items-stretch space-y-6 w-full min-h-[360px]">
          {news.length === 0 ? (
            <p className="text-(--foreground)">Nessuna news disponibile.</p>
          ) : (
            news.map((item, idx) => (
              <NewsCard
                key={item.id}
                item={item}
                delay={ready ? idx * 0.12 : 0}
                onClick={() => router.push("/news")}
              />
            ))
          )}
        </div>

        <AnimatePresence>
          {!ready && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center bg-(--background)"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              <div className="animate-pulse text-(--card-foreground)">Caricamento…</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
